import React, { useEffect, useRef, useState } from "react";
import PoweredBy from "../components/PoweredBy";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner"; // eslint-disable-line no-unused-vars
import { useAppSettings, resolveImageUrl } from "../context/AppSettingsContext";
import {
  Menu, Home, LogIn, ClipboardEdit, PenLine, Gift, Store, Newspaper, FileText, CalendarDays, Tag, ShoppingCart,
  Phone, Shield, ChevronRight, ChevronLeft, X as XIcon, ArrowLeft, LogOut, Share2, Mail, Clock, Award, Sparkles, LockKeyhole, LifeBuoy, Wallet,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tq = () => { const t = new URLSearchParams(window.location.search).get("t") || localStorage.getItem("kl_tenant"); return t ? { headers: { "X-Tenant": t } } : {}; };
const fmtDate = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : "");

export const MENU_PRIMARY = [
  { key: "home", to: "/home", label: "Home", icon: Home }, { key: "richiedi", to: "/richiedi-card", label: "Richiedi Card", icon: PenLine },
  { key: "accedi", to: "/cliente/accedi", label: "Accedi", icon: LogIn },
];
export const MENU_SECTIONS = [
  { key: "punti-vendita", to: "/home/sedi", label: "Punti vendita", icon: Store }, { key: "news", to: "/home/news", label: "News", icon: Newspaper },
  { key: "raccolta-punti", to: "/home/raccolta-punti", label: "Raccolta Punti", icon: Gift }, { key: "volantini", to: "/home/volantini", label: "Volantini", icon: FileText },
  { key: "iniziative", to: "/home/iniziative", label: "Iniziative", icon: CalendarDays }, { key: "offerte", to: "/home/offerte", label: "Offerte", icon: Tag },
  { key: "prodotti", to: "/home/prodotti", label: "Prodotti", icon: ShoppingCart },
  { key: "concorsi", to: "/home/concorsi", label: "Concorsi", icon: Award, requires: "contest_url" },
  { key: "scopri", to: "/scopri?src=menu", label: "Scopri il circuito", icon: Sparkles, requires: "landing_menu" },
];

const BRANCH_KEY = "kl_pub_branch";
// ?branch=<id> (dal portale cliente) preseleziona il punto vendita del cliente
(() => { try { const b = new URLSearchParams(window.location.search).get("branch"); if (b) localStorage.setItem(BRANCH_KEY, b); } catch { /* noop */ } })();
export const getPubBranch = () => localStorage.getItem(BRANCH_KEY) || "";
const bq = () => { const b = getPubBranch(); return b ? `&branch_id=${encodeURIComponent(b)}` : ""; };

export function BranchPicker({ onChange }) {
  const [branches, setBranches] = useState([]);
  const [val, setVal] = useState(getPubBranch());
  useEffect(() => { axios.get(`${API}/public/branches`, tq()).then(({ data }) => setBranches(data || [])).catch(() => {}); }, []);
  if (branches.length < 2) return null;
  const change = (v) => { setVal(v); if (v) localStorage.setItem(BRANCH_KEY, v); else localStorage.removeItem(BRANCH_KEY); onChange?.(v); };
  return (
    <label className="flex items-center gap-2 rounded-2xl bg-white px-3 py-2 shadow-sm ring-1 ring-zinc-200 text-sm" data-testid="public-branch-picker">
      <Store className="h-4 w-4 text-zinc-500" />
      <select value={val} onChange={(e) => change(e.target.value)} className="flex-1 bg-transparent outline-none" data-testid="public-branch-select">
        <option value="">Tutti i punti vendita</option>
        {branches.map((b) => <option key={b.id} value={b.id}>{(b.name || "").trim()}</option>)}
      </select>
    </label>
  );
}

export function useTiles() {
  const [tiles, setTiles] = useState({});
  const { settings } = useAppSettings();
  useEffect(() => { axios.get(`${API}/public/home-tiles`, tq()).then(({ data }) => setTiles(data || {})).catch(() => {}); }, []);
  const apply = (items) => items.filter((m) => !tiles[m.key]?.hidden && (!m.requires || settings?.[m.requires])).map((m) => ({ ...m, label: tiles[m.key]?.label || m.label, order: tiles[m.key]?.order ?? 99 })).sort((a, b) => a.order - b.order);
  return { tiles, apply };
}

export function useBrand() {
  const { settings } = useAppSettings();
  const brand = /^#[0-9a-fA-F]{6}$/.test(settings.client_brand_color || "") ? settings.client_brand_color : (settings.primary_color || "#1E40AF");
  return { settings, brand, logo: resolveImageUrl(settings.logo_url) };
}

const menuItemCls = "w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/90 hover:bg-white/10 transition-colors";
// Icona voce menu: miniatura 3D se disponibile per la chiave, altrimenti icona lucide
const MenuIcon = ({ k, icon: Icon }) => {
  const img = tileImg(k);
  return img ? <img src={img} alt="" className="h-8 w-8 rounded-lg object-cover shrink-0" draggable={false} /> : <span className="h-8 w-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><Icon className="h-[18px] w-[18px] opacity-90" /></span>;
};

// personal: voci dell'area riservata [{key,label,icon,onClick}] (sostituiscono Richiedi/Registra/Accedi); onLogout: mostra "Esci"
function SideMenu({ open, onClose, brand, settings, apply, personal, onLogout }) {
  const Item = ({ to, label, icon, chevron, testid, k }) => (
    <Link to={to} onClick={onClose} data-testid={testid || `home-menu-${label.toLowerCase().replace(/\s+/g, "-")}`} className={menuItemCls}>
      <MenuIcon k={k} icon={icon} /><span className="flex-1 font-medium">{label}</span>{chevron && <ChevronRight className="h-4 w-4 opacity-50" />}
    </Link>
  );
  const Action = ({ label, icon, onClick, testid, k }) => (
    <button type="button" onClick={() => { onClose(); onClick(); }} data-testid={testid} className={menuItemCls}>
      <MenuIcon k={k} icon={icon} /><span className="flex-1 font-medium">{label}</span>
    </button>
  );
  return (
    <>
      <div className={`fixed inset-0 z-40 bg-black/50 transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`} onClick={onClose} />
      <aside data-testid="home-side-menu" className={`fixed inset-y-0 left-0 z-50 w-72 overflow-y-auto p-3 space-y-1 transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"}`} style={{ background: brand }}>
        <div className="flex items-center justify-between px-2 py-2"><span className="font-heading font-bold truncate" style={{ color: "#fff" }}>{settings.app_name}</span><button onClick={onClose} aria-label="Chiudi" data-testid="home-menu-close" className="p-1.5 rounded-lg text-white/80 hover:bg-white/10"><XIcon className="h-5 w-5" /></button></div>
        {personal ? <>
          <Item to="/home" label="Home" icon={Home} chevron testid="home-menu-home" />
          {personal.map((p) => <Action key={p.key} {...p} k={p.key} testid={`home-menu-${p.key}`} />)}
        </> : apply(MENU_PRIMARY).map((m) => <Item key={m.to} {...m} k={m.key} testid={`home-menu-${m.key}`} chevron={m.key === "home"} />)}
        <div className="border-t border-white/15 my-2" />
        {apply(MENU_SECTIONS).map((m) => <Item key={m.to} {...m} k={m.key} testid={`home-menu-${m.key}`} />)}
        <div className="border-t border-white/15 my-2" />
        <Item to="/home/contatti" label="Contatti" icon={Phone} chevron />
        <Item to="/home/aiuto" label="Aiuto" icon={LifeBuoy} chevron testid="home-menu-aiuto" />
        <Item to="/home/gift-card" label="Gift Card" icon={Gift} chevron testid="home-menu-giftcard" />
        <Item to="/wallet" label="Card sul telefono (Wallet)" icon={Wallet} chevron testid="home-menu-wallet" />
        {settings.privacy_url ? <a href={resolveImageUrl(settings.privacy_url)} target="_blank" rel="noreferrer" className={menuItemCls} data-testid="home-menu-privacy"><MenuIcon k="privacy" icon={Shield} /><span className="flex-1 font-medium">Privacy Policy</span><ChevronRight className="h-4 w-4 opacity-50" /></a>
          : <Item to="/home/privacy" label="Privacy Policy" icon={Shield} k="privacy" chevron testid="home-menu-privacy" />}
        <div className="border-t border-white/15 my-2" />
        {onLogout ? <Action label="Esci" icon={LogOut} onClick={onLogout} testid="home-menu-logout" /> : <Item to="/cliente/accedi" label="Accedi" icon={LogOut} chevron />}
      </aside>
    </>
  );
}

// onBack: se presente il bottone indietro è un'azione (es. torna alla card) invece di un Link
// bottomNav: [{key,label,icon,onClick,active,menu}] → barra fissa in basso (menu:true apre il menu laterale)
// headerRight: nodo opzionale (es. campanella + avatar) mostrato prima del bottone menu
export function PublicShell({ children, title, back = "/home", onBack, testid, personal, onLogout, bottomNav, headerRight }) {
  const { settings, brand, logo } = useBrand();
  const { apply } = useTiles();
  const [open, setOpen] = useState(false);
  const backCls = "h-11 w-11 rounded-2xl bg-white shadow-sm ring-1 ring-zinc-200 flex items-center justify-center";
  const Logo = ({ left }) => (
    <div className={`flex items-center gap-2 min-w-0 ${left ? "justify-start" : "justify-center"}`} data-testid="home-logo">
      {logo ? <img src={logo} alt={settings.app_name} className="h-11 object-contain max-w-[200px]" /> : <span className="font-heading font-extrabold text-xl truncate" style={{ color: brand }}>{settings.app_name || "Loyalty Club"}</span>}
    </div>
  );
  return (
    <div className="min-h-screen text-zinc-900 bg-white" style={{ "--brand": brand }} data-testid={testid}>
      <SideMenu open={open} onClose={() => setOpen(false)} brand={brand} settings={settings} apply={apply} personal={personal} onLogout={onLogout} />
      <header className="sticky top-0 z-30 backdrop-blur bg-white/90 border-b border-zinc-200/60">
        <div className={`max-w-2xl mx-auto px-4 py-3 grid items-center ${title ? "grid-cols-[44px_1fr_auto]" : "grid-cols-[1fr_auto]"}`}>
          {title ? (onBack ? <button type="button" onClick={onBack} aria-label="Indietro" data-testid="home-back" className={backCls} style={{ color: brand }}><ArrowLeft className="h-5 w-5" /></button>
            : <Link to={back} aria-label="Indietro" data-testid="home-back" className={backCls} style={{ color: brand }}><ArrowLeft className="h-5 w-5" /></Link>) : <Logo left />}
          {title && <Logo />}
          <div className="flex items-center gap-2 justify-end">
            {headerRight}
            <button onClick={() => setOpen(true)} aria-label="Apri menu" data-testid="home-menu-button" className={backCls} style={{ color: brand }}><Menu className="h-5 w-5" /></button>
          </div>
        </div>
        {title && <div className="max-w-2xl mx-auto px-4 pb-3"><h1 className="font-heading font-extrabold text-2xl" data-testid="home-page-title">{title}</h1></div>}
      </header>
      <main className={`max-w-2xl mx-auto px-4 pt-4 space-y-6 ${bottomNav ? "pb-28" : "pb-20"}`}>
        {children}
        <div className="text-center pt-2 space-y-1.5" data-testid="public-staff-footer">
          {settings.support_email && (
            <div className="text-[11px] text-zinc-400" data-testid="public-support-footer">
              Assistenza: <a href={`mailto:${settings.support_email}`} className="hover:text-zinc-700 underline-offset-2 hover:underline" data-testid="public-support-email">{settings.support_email}</a>
            </div>
          )}
          <Link to="/login" className="inline-flex items-center gap-1.5 text-[11px] text-zinc-400 hover:text-zinc-700 transition-colors" data-testid="public-staff-link"><LockKeyhole className="h-3 w-3" /> Area riservata operatori</Link>
        </div>
      </main>
      {bottomNav && (
        <nav className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-zinc-200" data-testid="public-bottom-nav">
          <div className="max-w-2xl mx-auto grid px-2 py-2" style={{ gridTemplateColumns: `repeat(${bottomNav.length}, 1fr)` }}>
            {bottomNav.map((n) => (
              <button key={n.key} type="button" onClick={() => (n.menu ? setOpen(true) : n.onClick())} data-testid={`bottom-nav-${n.key}`}
                className="flex flex-col items-center gap-1 py-1 text-[11px] font-semibold" style={{ color: n.active ? brand : "#71717a" }}>
                <n.icon className="h-5 w-5" /><span>{n.label}</span>{n.active && <span className="h-0.5 w-6 rounded-full" style={{ background: brand }} />}
              </button>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}

// Palette pastello per icona (sfondo, colore) per chiave pulsante
const TILE_COLORS = {
  richiedi: ["#DBEAFE", "#1D4ED8"], registra: ["#CFFAFE", "#0E7490"], accedi: ["#DCFCE7", "#15803D"], "punti-vendita": ["#DBEAFE", "#1D4ED8"],
  news: ["#FFEDD5", "#C2410C"], volantini: ["#EDE9FE", "#6D28D9"], iniziative: ["#DBEAFE", "#1D4ED8"], offerte: ["#DCFCE7", "#15803D"], prodotti: ["#EDE9FE", "#6D28D9"],
  history: ["#DBEAFE", "#1D4ED8"], rewards: ["#FCE7F3", "#BE185D"], coupon: ["#DCFCE7", "#15803D"], branch: ["#FFEDD5", "#C2410C"], orders: ["#EDE9FE", "#6D28D9"],
  flyer: ["#DCFCE7", "#15803D"], menu: ["#CFFAFE", "#0E7490"], referral: ["#FEF3C7", "#B45309"], concorsi: ["#FCE7F3", "#BE185D"], chat: ["#DCFCE7", "#15803D"], privacy: ["#F1F5F9", "#475569"],
  catalogo: ["#FEF3C7", "#B45309"],
};
export const tileColor = (k) => TILE_COLORS[k] || ["#DBEAFE", "#1D4ED8"];
// Icone 3D fornite dal cliente (frontend/public/icons/home/*.png) per chiave pulsante
const ICON_KEYS = ["richiedi", "registra", "accedi", "punti-vendita", "raccolta-punti", "offerte", "news", "iniziative", "volantini", "eventi", "prodotti", "premi", "history", "orders", "referral", "chat", "privacy"];
const ICON_ALIAS = { rewards: "premi", coupon: "offerte", flyer: "volantini", menu: "prodotti", concorsi: "eventi", branch: "punti-vendita", catalogo: "raccolta-punti" };
export const tileImg = (k) => { const key = ICON_ALIAS[k] || k; return ICON_KEYS.includes(key) ? `/icons/home/${key}.png` : null; };

// Pulsante "soft": card bianca con icona 3D (se disponibile) o icona in cerchio pastello (griglia Home / azioni rapide)
const Dot = () => <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-red-500 ring-2 ring-white animate-pulse" data-testid="tile-badge" />;
// Badge "novità": confronta la data dell'ultimo contenuto con l'ultima visita salvata sul dispositivo
const seenKey = (k) => `home_seen_${k}`;
export const markSeen = (k) => { try { localStorage.setItem(seenKey(k), new Date().toISOString()); } catch (_) { /* no-op */ } };
const isFresh = (k, latest) => { if (!latest) return false; try { const s = localStorage.getItem(seenKey(k)); return !s || new Date(latest) > new Date(s); } catch (_) { return false; } };
const latestOf = (rows) => rows?.length ? rows.map((n) => n.created_at || n.date).filter(Boolean).sort().slice(-1)[0] : null;
export const SoftTile = ({ to, onClick, icon: Icon, label, testid, colorKey, badge, compact }) => {
  const [bg, fg] = tileColor(colorKey);
  const img = tileImg(colorKey);
  const size = compact ? "h-12 w-12" : "h-16 w-16";
  const inner = <>
    {img ? <span className={`relative ${size}`}><img src={img} alt="" className="h-full w-full rounded-2xl object-cover shadow-sm" draggable={false} />{badge === true ? <Dot /> : badge}</span>
      : <span className={`relative rounded-full flex items-center justify-center ${compact ? "h-12 w-12" : "h-14 w-14"}`} style={{ background: bg, color: fg }}><Icon className={compact ? "h-5 w-5" : "h-6 w-6"} />{badge === true ? <Dot /> : badge}</span>}
    <span className={`font-semibold text-zinc-800 text-center leading-tight ${compact ? "text-[11px]" : "text-[13px]"}`}>{label}</span>
  </>;
  const cls = `flex flex-col items-center gap-2 w-full transition active:scale-[0.97] ${compact ? "" : "rounded-2xl bg-white p-3 shadow-sm ring-1 ring-zinc-200 hover:shadow-md"}`;
  return to ? <Link to={to} className={cls} data-testid={testid}>{inner}</Link> : <button type="button" onClick={onClick} className={cls} data-testid={testid}>{inner}</button>;
};

// Riquadro "area" pastello con icona 3D o icona, etichetta e freccia (griglia 2 colonne)
export const AreaTile = ({ to, onClick, icon: Icon, label, testid, colorKey }) => {
  const [bg, fg] = tileColor(colorKey);
  const img = tileImg(colorKey);
  const inner = <>
    {img ? <img src={img} alt="" className="h-14 w-14 rounded-2xl object-cover shadow-sm" draggable={false} />
      : <span className="h-12 w-12 rounded-full bg-white/80 flex items-center justify-center" style={{ color: fg }}><Icon className="h-6 w-6" /></span>}
    <span className="flex items-center justify-between gap-2 w-full"><span className="font-semibold text-zinc-800 text-left leading-tight">{label}</span><ChevronRight className="h-4 w-4 shrink-0" style={{ color: fg }} /></span>
  </>;
  const cls = "flex flex-col items-start gap-3 rounded-2xl p-4 w-full transition hover:shadow-md active:scale-[0.98]";
  return to ? <Link to={to} className={cls} style={{ background: bg }} data-testid={testid}>{inner}</Link> : <button type="button" onClick={onClick} className={cls} style={{ background: bg }} data-testid={testid}>{inner}</button>;
};

export const SectionTitle = ({ children, brand, testid }) => (
  <h2 className="font-heading font-extrabold text-xl text-zinc-900" data-testid={testid}>{children}<span className="block h-1 w-8 rounded-full mt-1.5" style={{ background: brand }} /></h2>
);

// Banner promozionale a gradiente brand con testo e CTA (es. "I tuoi premi ti aspettano!")
export const PromoBanner = ({ title, subtitle, cta, to, onClick, brand, icon: Icon = Gift, image, testid }) => {
  const inner = <>
    {image && <img src={image} alt="" className="absolute inset-y-0 right-0 w-1/2 h-full object-cover" style={{ maskImage: "linear-gradient(90deg, transparent, #000 40%)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 40%)" }} />}
    <div className="relative flex items-center gap-4">
      <div className="flex-1 min-w-0 space-y-1">
        <div className="font-heading font-extrabold text-xl leading-tight italic">{title}</div>
        {subtitle && <div className="text-sm opacity-90">{subtitle}</div>}
        {cta && <span className="inline-flex items-center gap-1.5 mt-2 rounded-full bg-white px-4 py-2 text-sm font-semibold" style={{ color: brand }}>{cta} <ChevronRight className="h-4 w-4" /></span>}
      </div>
      {!image && <span className="h-16 w-16 rounded-2xl bg-white/15 flex items-center justify-center shrink-0"><Icon className="h-9 w-9" /></span>}
    </div>
  </>;
  const cls = "relative block w-full overflow-hidden rounded-3xl p-5 text-white text-left shadow-md hover:opacity-95 active:scale-[0.99] transition";
  const st = { background: `linear-gradient(135deg, ${brand} 0%, var(--client-brand-dark, ${brand}) 100%)` };
  return to ? <Link to={to} className={cls} style={st} data-testid={testid}>{inner}</Link> : <button type="button" onClick={onClick} className={cls} style={st} data-testid={testid}>{inner}</button>;
};

// Hero della Home pubblica: primo banner CMS come sfondo, titolo/sottotitolo/link del banner (fallback testi standard)
function HomeHero({ b, brand, apply, onCta }) {
  const img = b?.image_url?.trim() ? resolveImageUrl(b.image_url.trim()) : null;
  const bTitle = (b?.title || "").trim();
  const bSub = (b?.subtitle || "").trim();
  const title = bTitle || "La tua fedeltà premia.";
  const sub = bSub || "Accumula punti, scopri premi e vantaggi esclusivi.";
  const link = (b?.link || "").trim() || "/home/raccolta-punti";
  const feats = apply([
    { key: "raccolta-punti", to: "/home/raccolta-punti", label: "Raccogli punti", icon: Gift },
    { key: "accedi", to: "/cliente/accedi", label: "Scegli i tuoi premi", icon: Award },
    { key: "offerte", to: "/home/offerte", label: "Vivi vantaggi esclusivi", icon: Sparkles },
  ]);
  const imageOnly = !!b && !bTitle && !bSub && (img || b.video_url);
  const Cta = ({ children }) => link.startsWith("http") ? <a href={link} target="_blank" rel="noreferrer" onClick={onCta} className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 font-semibold shadow" style={{ color: brand }} data-testid="home-hero-cta">{children}</a>
    : <Link to={link} onClick={onCta} className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 font-semibold shadow" style={{ color: brand }} data-testid="home-hero-cta">{children}</Link>;
  const Feats = () => feats.length > 0 && <div className={`grid grid-cols-3 gap-2 ${imageOnly ? "" : "pt-3 border-t border-white/20"}`} data-testid="home-hero-features">
    {feats.map((f) => <Link key={f.key} to={f.to} className="flex flex-col items-center gap-1.5 text-center text-xs font-medium pt-3" data-testid={`home-hero-feat-${f.key}`}><span className="h-11 w-11 rounded-full bg-white/15 flex items-center justify-center"><f.icon className="h-5 w-5" /></span>{f.label}</Link>)}
  </div>;
  if (imageOnly) {
    // Immagine "già impaginata" (testi inclusi nella grafica): mostrata intera, cliccabile verso il link
    const media = b.video_url ? <video src={resolveImageUrl(b.video_url)} poster={img || undefined} autoPlay muted loop playsInline className="w-full h-auto block" data-testid="home-banner-video" />
      : <img src={img} alt={title} className="w-full h-auto block" data-testid="home-hero-image" />;
    return (
      <section className="-mx-4 -mt-4 overflow-hidden text-white" data-testid="home-hero" data-mode="image">
        {link.startsWith("http") ? <a href={link} target="_blank" rel="noreferrer" onClick={onCta} data-testid="home-hero-cta">{media}</a> : <Link to={link} onClick={onCta} data-testid="home-hero-cta">{media}</Link>}
        {feats.length > 0 && <div className="px-6 pb-6 -mt-px rounded-b-[2.5rem]" style={{ background: brand }}><Feats /></div>}
      </section>
    );
  }
  return (
    <section className="-mx-4 -mt-4 relative overflow-hidden rounded-b-[2.5rem] text-white" style={{ background: brand }} data-testid="home-hero">
      {b?.video_url ? <video src={resolveImageUrl(b.video_url)} poster={img || undefined} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" data-testid="home-banner-video" />
        : img && <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${brand}00 0%, ${brand}00 22%, ${brand}cc 52%, ${brand} 68%)` }} />
      <div className="relative px-6 pt-44 pb-8 space-y-4">
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl leading-[1.05]" style={{ color: b?.title_color || "#FFFFFF" }} data-testid="home-hero-title">{title}</h1>
        <p className="text-base opacity-95 max-w-xs" style={{ color: b?.subtitle_color || "#FFFFFF" }} data-testid="home-hero-subtitle">{sub}</p>
        <Cta>Scopri di più <ChevronRight className="h-4 w-4" /></Cta>
        <Feats />
      </div>
    </section>
  );
}

// Testata scorrevole: cicla tutti i banner (auto ogni 5s, swipe, puntini). 0 banner → testata standard
export function HeroSlider({ banners, brand, apply, interval = 5000, testid = "home-hero-slider", track = false }) {
  const [idx, setIdx] = useState(0);
  const n = banners.length;
  const touch = useRef(null);
  const seen = useRef(new Set());
  useEffect(() => { setIdx(0); }, [n]);
  // Statistiche: 1 visualizzazione per banner per visita, click sul CTA/immagine
  const cur = banners[Math.min(idx, Math.max(n - 1, 0))];
  useEffect(() => {
    if (!track || !cur?.id || seen.current.has(cur.id)) return;
    seen.current.add(cur.id);
    axios.post(`${API}/public/banners/${cur.id}/view`, null, tq()).catch(() => {});
  }, [track, cur?.id]);
  const onCta = () => { if (track && cur?.id) axios.post(`${API}/public/banners/${cur.id}/click`, null, tq()).catch(() => {}); };
  useEffect(() => { if (n > 1) { const t = setInterval(() => setIdx((i) => (i + 1) % n), interval); return () => clearInterval(t); } }, [n, interval, idx]);
  const onTouchStart = (e) => { touch.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touch.current == null || n < 2) return;
    const dx = e.changedTouches[0].clientX - touch.current; touch.current = null;
    if (Math.abs(dx) > 40) setIdx((i) => (i + (dx < 0 ? 1 : n - 1)) % n);
  };
  const b = banners[Math.min(idx, Math.max(n - 1, 0))];
  return (
    <div className="relative" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid={testid} data-index={idx} data-count={n}>
      <div key={b?.id || "default"} className="animate-fade-up"><HomeHero b={b} brand={brand} apply={apply} onCta={onCta} /></div>
      {n > 1 && (
        <div className="absolute left-0 right-0 -bottom-1 flex justify-center gap-1.5" data-testid="home-hero-dots">
          {banners.map((x, i) => <button key={x.id || i} type="button" onClick={() => setIdx(i)} aria-label={`Banner ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-zinc-700" : "w-1.5 bg-zinc-300"}`} data-testid={`home-hero-dot-${i}`} />)}
        </div>
      )}
    </div>
  );
}

export function BannerCarousel({ banners, brand, appName }) {
  const [idx, setIdx] = useState(0);
  const n = banners.length;
  useEffect(() => { if (n > 1) { const t = setInterval(() => setIdx((i) => (i + 1) % n), 5000); return () => clearInterval(t); } }, [n]);
  if (!n) return (
    <div className="rounded-3xl bg-white p-2 shadow-sm ring-1 ring-zinc-200" data-testid="home-banner-empty">
      <div className="rounded-2xl aspect-[16/10] flex flex-col items-center justify-center text-center p-6 text-white" style={{ background: brand }}>
        <Gift className="h-10 w-10 mb-3 opacity-90" /><div className="font-heading font-extrabold text-2xl">Raccolta Punti {appName}</div><div className="text-sm opacity-90 mt-1">Attiva la tua card e inizia a guadagnare punti su ogni acquisto.</div>
      </div>
    </div>
  );
  const b = banners[idx];
  const media = b.video_url
    ? <video src={resolveImageUrl(b.video_url)} poster={b.image_url ? resolveImageUrl(b.image_url) : undefined} autoPlay muted loop playsInline className="w-full aspect-[16/10] object-cover rounded-2xl bg-black" data-testid="home-banner-video" />
    : <img src={resolveImageUrl(b.image_url)} alt={b.title || "banner"} className="w-full aspect-[16/10] object-cover rounded-2xl" />;
  const img = media;
  return (
    <div data-testid="home-banner-carousel">
      <div className="relative rounded-3xl bg-white p-2 shadow-sm ring-1 ring-zinc-200">
        {b.link ? (b.link.startsWith("http") ? <a href={b.link} target="_blank" rel="noreferrer" data-testid="home-banner-link">{img}</a> : <Link to={b.link} data-testid="home-banner-link">{img}</Link>) : img}
        {(b.title || b.subtitle) && <div className="absolute left-5 right-5 bottom-5 text-white drop-shadow" data-testid="home-banner-caption">{b.title && <div className="font-heading font-extrabold text-xl" style={b.title_color ? { color: b.title_color } : undefined} data-testid="home-banner-title">{b.title}</div>}{b.subtitle && <div className="text-sm opacity-95" style={b.subtitle_color ? { color: b.subtitle_color } : undefined} data-testid="home-banner-subtitle">{b.subtitle}</div>}</div>}
        {n > 1 && <>
          <button onClick={() => setIdx((idx - 1 + n) % n)} aria-label="Precedente" className="absolute left-3 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white/80 flex items-center justify-center"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => setIdx((idx + 1) % n)} aria-label="Successivo" className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white/80 flex items-center justify-center"><ChevronRight className="h-4 w-4" /></button>
        </>}
      </div>
      {n > 1 && <div className="flex justify-center gap-2 mt-3">{banners.map((_, i) => <button key={i} onClick={() => setIdx(i)} aria-label={`Banner ${i + 1}`} className={`h-2 w-2 rounded-full ${i === idx ? "bg-zinc-700" : "bg-zinc-300"}`} />)}</div>}
    </div>
  );
}

// to → Link; onClick → button. badge: nodo opzionale in alto a destra del riquadro
export const Tile = ({ to, onClick, icon: Icon, label, brand, testid, badge }) => {
  const inner = <>
    <span className="relative w-full h-16 rounded-2xl flex items-center justify-center shadow-md transition-transform group-hover:-translate-y-0.5 group-active:scale-95" style={{ background: brand, color: "#fff" }}><Icon className="h-7 w-7" />{badge}</span>
    <span className="text-sm font-semibold text-zinc-800 text-center leading-tight">{label}</span>
  </>;
  const cls = "flex flex-col items-center gap-2 group w-full";
  return to ? <Link to={to} className={cls} data-testid={testid}>{inner}</Link> : <button type="button" onClick={onClick} className={cls} data-testid={testid}>{inner}</button>;
};

export function shareNews(n) {
  const t = new URLSearchParams(window.location.search).get("t") || localStorage.getItem("kl_tenant");
  const url = `${API}/public/share/news/${n.id}${t ? `?t=${t}` : ""}`;
  const text = `${n.title}\n${url}`;
  axios.post(`${API}/public/news/${n.id}/share`, null, tq()).catch(() => {});
  if (navigator.share) return navigator.share({ title: n.title, text: n.title, url }).catch(() => {});
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
}

const ShareButton = ({ n, brand, size = "sm" }) => (
  <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); shareNews(n); }} data-testid={`news-share-${n.id}`} aria-label="Condividi su WhatsApp"
    className={`inline-flex items-center gap-1.5 rounded-full font-semibold text-white shadow-sm hover:opacity-90 active:scale-95 transition ${size === "lg" ? "px-4 py-2 text-sm" : "px-3 py-1.5 text-xs"}`} style={{ background: "#25D366" }}>
    <Share2 className={size === "lg" ? "h-4 w-4" : "h-3.5 w-3.5"} /> {size === "lg" ? "Condividi su WhatsApp" : "Condividi"}
  </button>
);

export function NewsCard({ n, brand }) {
  const img = n.image_url ? resolveImageUrl(n.image_url) : null;
  return (
    <Link to={`/home/news/${n.id}`} className="block rounded-3xl bg-white p-2 shadow-sm ring-1 ring-zinc-200 hover:shadow-md transition-shadow" data-testid={`news-card-${n.id}`}>
      {img ? <img src={img} alt={n.title} className="w-full aspect-[16/10] object-cover rounded-2xl" /> : <div className="w-full aspect-[16/6] rounded-2xl flex items-center justify-center text-white" style={{ background: brand }}><Newspaper className="h-8 w-8 opacity-80" /></div>}
      <div className="px-3 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">{n.category === "offerte" ? "Offerta" : "News"} · {fmtDate(n.date || n.created_at)}{n.end_at && <span className="text-amber-600 normal-case tracking-normal"> · fino al {fmtDate(n.end_at)}</span>}</div>
          <ShareButton n={n} brand={brand} />
        </div>
        <div className="font-heading font-bold text-lg leading-snug mt-0.5">{n.title}</div>
        {n.body && <p className="text-sm text-zinc-600 mt-1 line-clamp-2">{n.body}</p>}
      </div>
    </Link>
  );
}

export default function PublicHome() {
  const { settings, brand } = useBrand();
  const { apply, tiles } = useTiles();
  const primary = apply(MENU_PRIMARY.filter((m) => m.key !== "home"));
  const sections = apply(MENU_SECTIONS.filter((m) => m.key !== "raccolta-punti"));
  void primary; void sections;
  const [banners, setBanners] = useState([]);
  const [news, setNews] = useState([]);
  const [fresh, setFresh] = useState({});
  useEffect(() => {
    axios.get(`${API}/public/news?category=offerte&limit=5`, tq()).then(({ data }) => setFresh((f) => ({ ...f, offerte: isFresh("offerte", latestOf(data)) }))).catch(() => {});
  }, []);
  useEffect(() => { setFresh((f) => ({ ...f, news: isFresh("news", latestOf(news)) })); }, [news]);
  const [branch, setBranch] = useState(getPubBranch());
  useEffect(() => {
    axios.get(`${API}/public/banners`, tq()).then(({ data }) => setBanners(data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    axios.get(`${API}/public/news?limit=3${bq()}`, tq()).then(({ data }) => setNews(data || [])).catch(() => {});
  }, [branch]);
  return (
    <PublicShell testid="public-home">
      <HeroSlider banners={banners} brand={brand} apply={apply} track />
      {!tiles["raccolta-punti"]?.hidden && (
        <div className="rounded-3xl p-5 space-y-3" style={{ background: "#EFF6FF" }} data-testid="home-raccolta-banner">
          <div className="flex items-center gap-3">
            <img src="/icons/home/raccolta-punti.png" alt="" className="h-14 w-14 rounded-2xl object-cover shadow-sm shrink-0" draggable={false} />
            <div className="font-heading font-extrabold text-lg leading-tight" style={{ color: brand }}>{tiles["raccolta-punti"]?.label || `Raccolta Punti ${settings.app_name || ""}`}</div>
          </div>
          <p className="text-sm text-zinc-700">Attiva la tua card e inizia a guadagnare punti su ogni acquisto.</p>
          <Link to="/richiedi-card" className="flex items-center justify-center gap-2 rounded-full px-5 py-3 text-white font-semibold shadow" style={{ background: brand }} data-testid="home-raccolta-cta">Attiva la tua card <ChevronRight className="h-4 w-4" /></Link>
        </div>
      )}
      <SectionTitle brand={brand} testid="home-grid-title">Scopri tutto il mondo {settings.app_name || ""}</SectionTitle>
      <section className="grid grid-cols-3 gap-3 !mt-3" data-testid="home-section-actions">
        {apply([...MENU_PRIMARY.filter((m) => m.key !== "home"), ...MENU_SECTIONS.filter((m) => m.key !== "raccolta-punti")]).map((m) => <SoftTile key={m.key} to={m.to} icon={m.icon} label={m.label} colorKey={m.key} testid={`home-btn-${m.key}`} badge={!!fresh[m.key]} />)}
      </section>
      <section className="space-y-3" data-testid="home-news">
        <div className="flex items-end justify-between"><SectionTitle brand={brand}>Ultime News</SectionTitle><Link to="/home/news" className="text-sm font-medium text-zinc-600 hover:text-zinc-900" data-testid="home-news-more">Mostra altro</Link></div>
        <BranchPicker onChange={setBranch} />
        {news.length === 0 ? <p className="text-sm text-zinc-500" data-testid="home-news-empty">Nessuna news pubblicata al momento.</p> : news.map((n) => <NewsCard key={n.id} n={n} brand={brand} />)}
      </section>
      <footer className="space-y-3 pt-2" data-testid="home-footer">
        <Link to="/home/raccolta-punti" className="block rounded-3xl overflow-hidden shadow-md ring-1 ring-zinc-200 hover:shadow-lg active:scale-[0.99] transition" data-testid="home-footer-banner">
          <img src="/icons/home/footer-banner.jpg" alt="La tua fedeltà premia!" className="w-full h-auto block" />
        </Link>
        <p className="text-center text-xs text-zinc-500">Più acquisti. Più vantaggi. Sempre.</p>
        <div className="flex justify-center pt-1"><PoweredBy testid="home-powered-by" /></div>
      </footer>
    </PublicShell>
  );
}

export function PublicNewsList({ category }) {
  const { brand } = useBrand();
  const [rows, setRows] = useState(null);
  const [branch, setBranch] = useState(getPubBranch());
  useEffect(() => { markSeen(category || "news"); }, [category]);
  useEffect(() => { axios.get(`${API}/public/news?x=1${category ? `&category=${category}` : ""}${bq()}`, tq()).then(({ data }) => setRows(data || [])).catch(() => setRows([])); }, [category, branch]);
  const title = category === "offerte" ? "Offerte" : "News";
  return (
    <PublicShell title={title} testid={`public-${title.toLowerCase()}`}>
      <BranchPicker onChange={setBranch} />
      {rows === null ? <p className="text-sm text-zinc-500">Carico…</p> : rows.length === 0 ? <p className="text-sm text-zinc-500" data-testid="news-empty">{category === "offerte" ? "Nessuna offerta attiva al momento." : "Nessuna news pubblicata."}</p> : rows.map((n) => <NewsCard key={n.id} n={n} brand={brand} />)}
    </PublicShell>
  );
}

export function PublicNewsDetail() {
  const { id } = useParams();
  const { brand } = useBrand();
  const [n, setN] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { axios.get(`${API}/public/news/${id}`, tq()).then(({ data }) => { setN(data); axios.post(`${API}/public/news/${id}/view`, null, tq()).catch(() => {}); }).catch(() => setErr(true)); }, [id]);
  return (
    <PublicShell title={n?.category === "offerte" ? "Offerta" : "News"} back={n?.category === "offerte" ? "/home/offerte" : "/home/news"} testid="public-news-detail">
      {err && <p className="text-sm text-zinc-500">Contenuto non disponibile.</p>}
      {n && <article className="rounded-3xl bg-white p-2 shadow-sm ring-1 ring-zinc-200" data-testid="news-detail">
        {n.image_url && <img src={resolveImageUrl(n.image_url)} alt={n.title} className="w-full rounded-2xl object-cover" />}
        <div className="px-3 py-4 space-y-2">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">{fmtDate(n.date || n.created_at)}</div>
          <h2 className="font-heading font-extrabold text-2xl" data-testid="news-detail-title">{n.title}</h2>
          <p className="text-zinc-700 whitespace-pre-line leading-relaxed">{n.body}</p>
          <div className="flex flex-wrap gap-2 pt-2">
            {n.link && <a href={n.link} target="_blank" rel="noreferrer" className="inline-block px-4 py-2 rounded-full text-white text-sm font-semibold" style={{ background: brand }} data-testid="news-detail-link">Scopri di più</a>}
            <ShareButton n={n} brand={brand} size="lg" />
          </div>
        </div>
      </article>}
    </PublicShell>
  );
}

export function PublicIniziative() {
  const { brand } = useBrand();
  const [rows, setRows] = useState(null);
  useEffect(() => { axios.get(`${API}/public/promos`, tq()).then(({ data }) => setRows((data || []).filter(Boolean))).catch(() => setRows([])); }, []);
  return (
    <PublicShell title="Iniziative" testid="public-iniziative">
      {rows === null ? <p className="text-sm text-zinc-500">Carico…</p> : rows.length === 0 ? <p className="text-sm text-zinc-500" data-testid="iniziative-empty">Nessuna iniziativa attiva al momento.</p> : rows.map((p, i) => (
        <div key={i} className="rounded-3xl bg-white p-2 shadow-sm ring-1 ring-zinc-200" data-testid={`iniziativa-${i}`}>
          {p.image_url ? <img src={resolveImageUrl(p.image_url)} alt={p.title} className="w-full aspect-[16/10] object-cover rounded-2xl" /> : <div className="w-full aspect-[16/6] rounded-2xl flex items-center justify-center text-white" style={{ background: brand }}><CalendarDays className="h-8 w-8 opacity-80" /></div>}
          <div className="px-3 py-3">{p.eyebrow && <div className="text-[11px] uppercase tracking-widest text-zinc-500">{p.eyebrow}</div>}<div className="font-heading font-bold text-lg">{p.title}</div>{p.subtitle && <p className="text-sm text-zinc-600 mt-1">{p.subtitle}</p>}</div>
        </div>
      ))}
    </PublicShell>
  );
}

export function PublicProducts() {
  const nav = useNavigate();
  const { brand } = useBrand();
  const [branches, setBranches] = useState(null);
  useEffect(() => { axios.get(`${API}/public/branches`, tq()).then(({ data }) => { const b = data || []; if (b.length === 1) nav(`/menu-sede/${b[0].id}`, { replace: true }); else setBranches(b); }).catch(() => setBranches([])); }, [nav]);
  return (
    <PublicShell title="Prodotti" testid="public-prodotti">
      <p className="text-sm text-zinc-600">Scegli il punto vendita per vedere i prodotti disponibili.</p>
      {branches === null ? <p className="text-sm text-zinc-500">Carico…</p> : branches.length === 0 ? <p className="text-sm text-zinc-500" data-testid="prodotti-empty">Nessun punto vendita disponibile.</p> : branches.map((b) => (
        <Link key={b.id} to={`/menu-sede/${b.id}`} className="flex items-center gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-zinc-200 hover:shadow-md" data-testid={`prodotti-branch-${b.id}`}>
          <span className="h-10 w-10 rounded-xl flex items-center justify-center text-white" style={{ background: brand }}><Store className="h-5 w-5" /></span>
          <span className="flex-1"><div className="font-semibold">{b.name}</div>{b.address && <div className="text-xs text-zinc-500">{b.address}</div>}</span><ChevronRight className="h-4 w-4 text-zinc-400" />
        </Link>
      ))}
    </PublicShell>
  );
}

export function PublicRaccoltaPunti() {
  const { settings, brand } = useBrand();
  const [cfg, setCfg] = useState(null);
  useEffect(() => { axios.get(`${API}/public/raccolta`, tq()).then(({ data }) => setCfg(data)).catch(() => setCfg({})); }, []);
  if (!cfg) return <PublicShell title="Raccolta Punti" testid="public-raccolta"><p className="text-sm text-zinc-500">Carico…</p></PublicShell>;
  const title = cfg.title || `Raccolta Punti ${settings.app_name || ""}`.trim();
  const steps = (cfg.steps || []).filter(Boolean);
  return (
    <PublicShell title="Raccolta Punti" testid="public-raccolta">
      <div className="rounded-3xl p-6 text-white shadow-md" style={{ background: brand }} data-testid="raccolta-hero"><Gift className="h-10 w-10 mb-2 opacity-90" /><div className="font-heading font-extrabold text-2xl" data-testid="raccolta-title">{title}</div>{cfg.subtitle && <p className="text-sm opacity-90 mt-1" data-testid="raccolta-subtitle">{cfg.subtitle}</p>}</div>
      {cfg.intro && <p className="text-sm text-zinc-700 whitespace-pre-line" data-testid="raccolta-intro">{cfg.intro}</p>}
      <ol className="space-y-3" data-testid="raccolta-steps">{steps.map((t, i) => <li key={i} className="flex items-start gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-zinc-200" data-testid={`raccolta-step-${i}`}><span className="mt-0.5 shrink-0 h-7 w-7 rounded-full text-white text-xs font-bold flex items-center justify-center" style={{ background: brand }}>{i + 1}</span><span className="text-sm text-zinc-700">{t}</span></li>)}</ol>
      {cfg.footer && <p className="text-xs text-zinc-500 whitespace-pre-line" data-testid="raccolta-footer">{cfg.footer}</p>}
      <div className="grid grid-cols-2 gap-3"><Link to="/richiedi-card" className="text-center px-4 py-3 rounded-2xl text-white font-semibold" style={{ background: brand }} data-testid="raccolta-cta-richiedi">{cfg.cta_primary || "Richiedi Card"}</Link><Link to="/cliente/accedi" className="text-center px-4 py-3 rounded-2xl bg-white ring-1 ring-zinc-200 font-semibold" data-testid="raccolta-cta-accedi">{cfg.cta_secondary || "Accedi"}</Link></div>
    </PublicShell>
  );
}

export function PublicContacts() {
  const { brand } = useBrand();
  const [branches, setBranches] = useState([]);
  const [cfg, setCfg] = useState({ branches: {} });
  useEffect(() => {
    axios.get(`${API}/public/branches`, tq()).then(({ data }) => setBranches(data || [])).catch(() => {});
    axios.get(`${API}/public/contacts`, tq()).then(({ data }) => setCfg(data || { branches: {} })).catch(() => {});
  }, []);
  const Row = ({ icon: Icon, children, href, testid }) => (
    <a href={href} className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: brand }} data-testid={testid}><Icon className="h-4 w-4" /> {children}</a>
  );
  const hasGlobal = cfg.intro || cfg.email || cfg.phone || cfg.whatsapp;
  return (
    <PublicShell title="Contatti" testid="public-contatti">
      {hasGlobal && (
        <div className="rounded-3xl p-5 text-white shadow-md space-y-2" style={{ background: brand }} data-testid="contatti-intro">
          {cfg.intro && <p className="text-sm whitespace-pre-line">{cfg.intro}</p>}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm font-medium">
            {cfg.phone && <a href={`tel:${cfg.phone}`} className="inline-flex items-center gap-1" data-testid="contatti-phone"><Phone className="h-4 w-4" /> {cfg.phone}</a>}
            {cfg.whatsapp && <a href={`https://wa.me/${cfg.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1" data-testid="contatti-whatsapp"><Share2 className="h-4 w-4" /> WhatsApp</a>}
            {cfg.email && <a href={`mailto:${cfg.email}`} className="inline-flex items-center gap-1 break-all" data-testid="contatti-email"><Mail className="h-4 w-4" /> {cfg.email}</a>}
          </div>
        </div>
      )}
      {branches.length === 0 && !hasGlobal && <p className="text-sm text-zinc-500">Nessun contatto disponibile.</p>}
      {branches.map((b) => { const x = (cfg.branches || {})[b.id] || {}; return (
        <div key={b.id} className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-zinc-200 space-y-1.5" data-testid={`contatti-${b.id}`}>
          <div className="font-semibold">{(b.name || "").trim()}</div>
          {b.address && <div className="text-sm text-zinc-600">{b.address}</div>}
          {x.hours && <div className="text-sm text-zinc-700 whitespace-pre-line flex items-start gap-1.5" data-testid={`contatti-hours-${b.id}`}><Clock className="h-4 w-4 mt-0.5 text-zinc-400 shrink-0" /> {x.hours}</div>}
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {(x.phone || b.phone) && <Row icon={Phone} href={`tel:${x.phone || b.phone}`} testid={`contatti-phone-${b.id}`}>{x.phone || b.phone}</Row>}
            {x.email && <Row icon={Mail} href={`mailto:${x.email}`} testid={`contatti-email-${b.id}`}>{x.email}</Row>}
          </div>
          {x.note && <p className="text-xs text-zinc-500 whitespace-pre-line" data-testid={`contatti-note-${b.id}`}>{x.note}</p>}
        </div>
      ); })}
    </PublicShell>
  );
}

// Concorso nella Home pubblica: anche i non iscritti giocano; CTA per richiedere la card
export function PublicContest() {
  const { settings, brand } = useBrand();
  const url = settings.contest_url;
  const title = settings.contest_title || "Concorsi";
  return (
    <PublicShell title={title} testid="public-contest">
      {!url ? (
        <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-zinc-200 text-sm text-zinc-600" data-testid="public-contest-empty">Nessun concorso attivo al momento. Torna a trovarci!</div>
      ) : (
        <>
          <div className="rounded-3xl p-5 text-white shadow-md" style={{ background: brand }} data-testid="public-contest-hero">
            <Award className="h-8 w-8 mb-2 opacity-90" />
            <div className="font-heading font-extrabold text-xl">Gioca e vinci</div>
            <p className="text-sm opacity-90 mt-1">Partecipa al concorso: è aperto a tutti. Con la card fedeltà {settings.contest_bonus_points > 0 ? `ricevi anche +${settings.contest_bonus_points} punti a partecipazione` : "accumuli punti e vantaggi esclusivi"}.</p>
          </div>
          <div className="rounded-2xl overflow-hidden bg-black shadow-md ring-1 ring-zinc-200" data-testid="public-contest-embed">
            <iframe src={url} title={title} width="100%" height="820" style={{ border: 0, display: "block" }} allow="camera" referrerPolicy="no-referrer-when-downgrade" data-testid="public-contest-iframe" />
          </div>
          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-zinc-200 space-y-3" data-testid="public-contest-cta">
            <div className="font-heading font-bold text-zinc-900">Non hai ancora la card?</div>
            <p className="text-sm text-zinc-600">Richiedila gratis: punti su ogni acquisto, coupon riservati{settings.contest_bonus_points > 0 ? ` e +${settings.contest_bonus_points} punti ogni volta che partecipi al concorso dal tuo portale` : ""}.</p>
            <div className="grid grid-cols-2 gap-2">
              <Link to="/richiedi-card" className="rounded-full py-3 text-center text-sm font-semibold text-white shadow" style={{ background: brand }} data-testid="public-contest-request-card">Richiedi la card</Link>
              <Link to="/cliente/accedi" className="rounded-full py-3 text-center text-sm font-semibold ring-1 ring-zinc-300 text-zinc-800" data-testid="public-contest-login">Ho già la card</Link>
            </div>
          </div>
        </>
      )}
    </PublicShell>
  );
}


export function PublicPrivacy() {
  const { settings } = useBrand();
  return (
    <PublicShell title="Privacy Policy" testid="public-privacy">
      <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-zinc-200 text-sm text-zinc-700 whitespace-pre-line">
        {settings.privacy_text || `Informativa sul trattamento dei dati personali del programma fedeltà ${settings.app_name || ""}. I dati raccolti (anagrafica, contatti, acquisti) sono trattati per la gestione della card, l'accredito dei punti e, previo consenso, per comunicazioni promozionali. Puoi esercitare i tuoi diritti (accesso, rettifica, cancellazione) contattando il titolare presso i punti vendita.`}
      </div>
    </PublicShell>
  );
}
